import React, { useCallback, useEffect, useState } from "react";
import { Dropdown, Spin, Table, Tag } from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CloseOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import EngagementService, {
  EngagementDto,
} from "../../../ClientView/services/EngagementService";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import styles from "./EngagementList.module.css";

// ── Type badge helpers ────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  Audit:      "#8B2500",
  Tax:        "#2563eb",
  Compliance: "#7c3aed",
  Consulting: "#16a34a",
  Advisory:   "#0891b2",
};

function TypeBadge({ type }: { type: string }) {
  const color = TYPE_COLORS[type] ?? "#64748b";
  return (
    <span
      style={{
        background: `${color}28`,
        color,
        border: `1px solid ${color}55`,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
}

function StatusTag({ status }: { status: string }) {
  const map: Record<string, string> = {
    Active:    "#22c55e",
    Draft:     "#f59e0b",
    Completed: "#94a3b8",
  };
  const color = map[status] ?? "#ADADAD";
  return (
    <Tag
      style={{
        color,
        borderColor: color,
        background: "transparent",
        borderRadius: 4,
        fontWeight: 500,
        fontSize: 11,
      }}
    >
      {status || "—"}
    </Tag>
  );
}

// ── EngagementList ────────────────────────────────────────────────────────────

export const EngagementList: React.FC = () => {
  const [engagements, setEngagements]   = useState<EngagementDto[]>([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [search, setSearch]             = useState("");
  const [typeFilter, setTypeFilter]     = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();
  const { selectedClient } = usePartnershipContext();

  const fetchEngagements = useCallback(async () => {
    setIsLoading(true);
    try {
      // Pass selectedClient?.id as the tenant override to filter by client.
      const data = await EngagementService.getEngagements(selectedClient?.id);
      setEngagements(data);
    } catch {
      setEngagements([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedClient]);

  useEffect(() => { fetchEngagements(); }, [fetchEngagements]);

  const types    = Array.from(new Set(engagements.map((e) => e.engagementType)));
  const statuses = Array.from(new Set(engagements.map((e) => e.status)));

  const filtered = engagements.filter((e) => {
    if (typeFilter !== "all" && e.engagementType !== typeFilter) return false;
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (
      search &&
      !e.description.toLowerCase().includes(search.toLowerCase()) &&
      !e.engagementId.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const columns: ColumnsType<EngagementDto> = [
    {
      title: "Engagement ID",
      dataIndex: "engagementId",
      width: 150,
      render: (v: string) => <span className={styles.mono}>{v}</span>,
    },
    {
      title: "Engagement Name",
      key: "name",
      render: (_, r) => (
        <span className={styles.engName}>{r.description}</span>
        // description serves as engagement name — no separate name field in EngagementDto
      ),
    },
    {
      title: "Type",
      dataIndex: "engagementType",
      width: 130,
      render: (v: string) => <TypeBadge type={v} />,
    },
    {
      // taxYear shown instead of date range — startDate/endDate not in EngagementDto
      title: "Period",
      dataIndex: "taxYear",
      width: 90,
      render: (v: number) => <span className={styles.mono}>{v}</span>,
    },
    {
      // Lead partner not in EngagementDto
      title: "Lead Partner",
      key: "lead",
      width: 130,
      render: () => <span className={styles.cellMuted}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (v: string) => <StatusTag status={v} />,
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "view",
            label: "View details",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              navigate(`/engagements/${record.id}`, {
                state: { engagement: record },
              });
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button
              className={styles.actionBtn}
              onClick={(e) => e.stopPropagation()}
              aria-label="Actions"
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Engagements</h1>
        <button
          className={styles.btnPrimary}
          onClick={() => navigate("/engagements/create")}
        >
          <PlusOutlined style={{ marginRight: 6 }} />
          Create Engagement
        </button>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchWrap}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search engagements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className={styles.filterSelect}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">Type: All</option>
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Status: All</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {(typeFilter !== "all" || statusFilter !== "all") && (
          <span
            className={styles.filterChip}
            onClick={() => { setTypeFilter("all"); setStatusFilter("all"); }}
          >
            <CloseOutlined style={{ marginRight: 4, fontSize: 10 }} />
            Clear filters
          </span>
        )}
      </div>

      {isLoading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : (
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          pagination={false}
          onRow={(record) => ({
            onClick: () =>
              navigate(`/engagements/${record.id}`, {
                state: { engagement: record },
              }),
            style: { cursor: "pointer" },
          })}
          className={styles.engTable}
          size="small"
        />
      )}

      <div className={styles.tableFooter}>
        Showing {filtered.length} of {engagements.length} engagement
        {engagements.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};
